import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { getSessionId } from "./authService";
import {
  EXTERNAL_SUBSCRIPTION_PAYMENT_CALLBACK_TO_HR,
  SUBSCRIPTION_PAYMENT_PACKAGE_SESSION_KEY,
  normalizeSubscriptionPackageCodeForPaymentLink,
  resolveExternalSubscriptionPaymentLink,
} from "../utils/resolveExternalSubscriptionPaymentLink";

export {
  normalizeSubscriptionPackageCodeForPaymentLink,
  resolveExternalSubscriptionPaymentLink,
  SUBSCRIPTION_PAYMENT_SUCCESS_PATH,
  SUBSCRIPTION_PAYMENT_PACKAGE_SESSION_KEY,
} from "../utils/resolveExternalSubscriptionPaymentLink";

/**
 * שכבת API ל-billing status של סוכן מחובר.
 *
 * מתכתב עם GET /api/agent/billing/status בבקאנד.
 * החוזה יציב (מוגדר ב-services/agent_billing_status_service.py):
 *   {
 *     agentId: number,
 *     packageCode: "BASIC" | "PRO" | null,
 *     subscriptionStatus: "active_paid" | "legacy_unbilled" | ... | null,
 *     billingMigrationRequired: boolean,
 *     hasActivePaymentCoverage: boolean,
 *     nextChargeAt: string (ISO) | null,
 *     lastChargeAt: string (ISO) | null,
 *   }
 *
 * מיועד ל-FE banner / gate logic ולשלב הבא של payment setup flow.
 *
 * כשל בשרת: HTTP 200 עם { ok: false, error_code, message } (לא 500) — ראה normalize ב-AgentBillingStatusContext.
 */

const BILLING_STATUS_PATH = "/api/agent/billing/status";
const BILLING_DEFER_PATH = "/api/agent/billing/defer";
const BILLING_SETUP_PATH = "/api/agent/billing/setup";
const BILLING_STATUS_TIMEOUT_MS = 30000;

function billingStatusUrl() {
  return resolveApiUrl(BILLING_STATUS_PATH);
}

function billingDeferUrl() {
  return resolveApiUrl(BILLING_DEFER_PATH);
}

function billingSetupUrl() {
  return resolveApiUrl(BILLING_SETUP_PATH);
}

/**
 * מחזיר billing status של הסוכן המחובר.
 * זורק אם אין session — הקומפוננטה הקוראת אחראית לטיפול שקט.
 * @returns {Promise<object>}
 */
export async function getAgentBillingStatus() {
  const sessionID = await getSessionId();
  if (!sessionID) {
    throw new Error("agent_billing_status_no_session");
  }
  const sid = String(sessionID).trim();
  const { data } = await axios.get(billingStatusUrl(), {
    headers: {
      "X-Session-ID": sid,
    },
    withCredentials: true,
    timeout: BILLING_STATUS_TIMEOUT_MS,
  });
  return data;
}

/**
 * נקרא רק כאשר המשתמש לחץ במפורש "המשך למערכת" ב-modal ה-billing migration.
 * מגדיל defer count ב-1, ואחרי ה-4 חוסם את הסוכן. לא נקרא ע״י רענון/טעינה/visibilitychange.
 * @returns {Promise<object>} — אותו חוזה כמו getAgentBillingStatus (עם הערכים המעודכנים)
 */
/**
 * פותח HYP vault setup session עבור סוכן legacy_unbilled.
 *
 * תשובה תקינה:
 *   { ok: true, setupUrl: "https://...", provider: "hyp", paymentMethodId: "uuid" }
 *
 * תגובות שגיאה מוכרות (מטופלות על-ידי הקורא):
 *   * 409 CONFLICT (code="not_legacy_unbilled") — הסוכן כבר לא במצב legacy
 *   * 500 BILLING_SETUP_ERROR — HYP לא זמין
 */
export async function startAgentBillingSetup() {
  const sessionID = await getSessionId();
  if (!sessionID) {
    throw new Error("agent_billing_status_no_session");
  }
  const sid = String(sessionID).trim();
  const { data } = await axios.post(
    billingSetupUrl(),
    {},
    {
      headers: {
        "Content-Type": "application/json",
        "X-Session-ID": sid,
      },
      withCredentials: true,
      timeout: 20000,
    },
  );
  return data;
}

const PAYMENT_METHODS_SETUP_SESSION_PATH = "/api/payment-methods/setup-session";

function paymentMethodsSetupSessionUrl() {
  return resolveApiUrl(PAYMENT_METHODS_SETUP_SESSION_PATH);
}

/** מחזיר URL ל-HYP מתשובת setup (billing/setup או payment-methods/setup-session). */
export function resolveAgentBillingSetupRedirectUrl(payload) {
  if (!payload || typeof payload !== "object") return null;
  const url =
    payload.setupUrl ||
    payload.redirectUrl ||
    payload.paymentPageUrl ||
    payload.payment_url;
  const s = url != null ? String(url).trim() : "";
  return s || null;
}

/**
 * HYP vault setup לסוכן מחובר (כל סטטוס מנוי) — POST /api/payment-methods/setup-session.
 * @returns {Promise<{ ok?: boolean, paymentMethodId?: string, redirectUrl?: string, paymentPageUrl?: string }>}
 */
export async function startAgentPaymentMethodVaultSetup() {
  const sessionID = await getSessionId();
  if (!sessionID) {
    throw new Error("agent_billing_status_no_session");
  }
  const sid = String(sessionID).trim();
  const { data } = await axios.post(
    paymentMethodsSetupSessionUrl(),
    {},
    {
      headers: {
        "Content-Type": "application/json",
        "X-Session-ID": sid,
      },
      withCredentials: true,
      timeout: 20000,
    },
  );
  return data;
}

/**
 * Flow «הגדר אמצעי תשלום» — לינק חיצוני קבוע לפי חבילה (Morning / mrng.to).
 * ללא POST /api/payment-methods/setup-session וללא pending_setup מקומי.
 *
 * @param {{ packageCode?: string|null }} [options]
 * @returns {{ ok: boolean, setupUrl?: string, provider?: string, packageCode?: string|null, error?: string, callbackToHotelsRiver?: boolean }}
 */
export function openAgentPaymentMethodSetup({ packageCode } = {}) {
  const tier = normalizeSubscriptionPackageCodeForPaymentLink(packageCode);
  const setupUrl = resolveExternalSubscriptionPaymentLink(packageCode);
  if (!tier) {
    return {
      ok: false,
      error: "unknown_package_code",
      packageCode: packageCode ?? null,
    };
  }
  if (!setupUrl) {
    return {
      ok: false,
      error: "external_payment_link_not_configured",
      packageCode: tier,
    };
  }
  try {
    sessionStorage.setItem(SUBSCRIPTION_PAYMENT_PACKAGE_SESSION_KEY, tier);
  } catch {
    /* ignore */
  }
  return {
    ok: true,
    setupUrl,
    provider: "external_morning",
    packageCode: tier,
    callbackToHotelsRiver: EXTERNAL_SUBSCRIPTION_PAYMENT_CALLBACK_TO_HR,
  };
}

export async function deferAgentBillingMigration() {
  const sessionID = await getSessionId();
  if (!sessionID) {
    throw new Error("agent_billing_status_no_session");
  }
  const sid = String(sessionID).trim();
  const { data } = await axios.post(
    billingDeferUrl(),
    {},
    {
      headers: {
        "Content-Type": "application/json",
        "X-Session-ID": sid,
      },
      withCredentials: true,
      timeout: 15000,
    },
  );
  return data;
}
